import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DesktopHeader from './DesktopHeader';
import Sidebar from './Sidebar';
import MobileNavigation from './MobileNavigation';
import type { MenuItem } from './types';
import { scrollToSection } from '../../utils/scroll';

interface CMSLayoutProps {
  children: React.ReactNode;
  menuItems: MenuItem[];
  activeSection: string;
  onSectionChange: (section: string) => void;
}

export const CMSLayout: React.FC<CMSLayoutProps> = ({
  children,
  menuItems,
  activeSection,
  onSectionChange
}) => {
  const handleSectionChange = (section: string) => {
    onSectionChange(section);
    scrollToSection('cms-content');
  };

  return (
    <div className="min-h-screen bg-background pt-16 relative">
      <DesktopHeader />
      <main className="px-6 pb-24 relative">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-6">
            <div className="hidden md:block w-64 shrink-0">
              <Sidebar
                menuItems={menuItems}
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              />
            </div>

            <div className="flex-1">
              <div id="cms-content" className="relative bg-card/20 rounded-2xl p-6 shadow-xl border border-white/5 overflow-hidden min-h-[500px]">
                {/* Gradiente base escuro */}
                <div className="absolute inset-0 -z-10">
                  <div className="absolute inset-0 bg-gradient-to-br from-background via-background/98 to-background" />
                  
                  {/* Padrão hexagonal com brilho */}
                  <div className="absolute inset-0"
                    style={{
                      backgroundImage: `
                        linear-gradient(60deg, transparent 25%, rgba(0,149,255,0.05) 50%, transparent 75%),
                        linear-gradient(-60deg, transparent 25%, rgba(0,149,255,0.05) 50%, transparent 75%)
                      `,
                      backgroundSize: '100px 173px',
                      backgroundPosition: '0 0, 0 0, 50px 86.5px, 50px 86.5px'
                    }}
                  />

                  {/* Pontos de brilho nos vértices */}
                  <div className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at center, rgba(0,149,255,0.05) 1px, transparent 2px)`,
                      backgroundSize: '50px 86.5px',
                      backgroundPosition: '25px 43.25px'
                    }}
                  />

                  {/* Efeito de brilho dinâmico */}
                  <div className="absolute inset-0"
                    style={{
                      background: 'radial-gradient(circle at 50% 50%, rgba(0,149,255,0.03) 0%, transparent 50%)'
                    }}
                  />

                  {/* Linhas de conexão suaves */}
                  <div className="absolute inset-0 opacity-5"
                    style={{
                      backgroundImage: `
                        linear-gradient(30deg, transparent 45%, rgba(0,149,255,0.05) 50%, transparent 55%),
                        linear-gradient(-30deg, transparent 45%, rgba(0,149,255,0.05) 50%, transparent 55%)
                      `,
                      backgroundSize: '100px 173px'
                    }}
                  />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="min-h-[500px] relative"
                  >
                    {children}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur-sm border-t border-white/10">
        <MobileNavigation
          menuItems={menuItems}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
        />
      </div>
    </div>
  );
}; 